import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Typography,
  message,
} from 'antd'
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { v4 as uuid } from 'uuid'
import fetchAPI from './api'

const kinds = [
  { value: 'measurement', label: 'Замер' },
  { value: 'supervision', label: 'Шеф-монтаж' },
  { value: 'installation', label: 'Монтаж' },
  { value: 'repair', label: 'Ремонтные работы' },
]
const errorText = (error: any) => error?.message || 'Не удалось сохранить данные'
const money = (value: unknown) =>
  value == null ? '—' : Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 })

export default function ClaimsAccountingModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [month, setMonth] = useState(dayjs().startOf('month'))
  const [records, setRecords] = useState<any[]>([])
  const [manager, setManager] = useState(false)
  const [period, setPeriod] = useState<any>({ revision: 0 })
  const [unknown, setUnknown] = useState(0)
  const [busy, setBusy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [periodDirty, setPeriodDirty] = useState(false)
  const [editor, setEditor] = useState<any>(undefined)
  const [editorError, setEditorError] = useState<string | null>(null)
  const [editorBusy, setEditorBusy] = useState(false)
  const [periodForm] = Form.useForm()
  const [serviceForm] = Form.useForm()
  const requestId = useRef(uuid())
  const generation = useRef(0)

  const load = useCallback(async () => {
    const current = ++generation.current
    setBusy(true)
    setError(null)
    try {
      const options = await fetchAPI('/api/claims-accounting/options')
      const [services, check] = await Promise.all([
        fetchAPI(`/api/claims-accounting/services/${month.format('YYYY-MM')}`),
        options.can_manage_periods
          ? fetchAPI(`/api/claims-accounting/period/${month.format('YYYY-MM')}`)
          : Promise.resolve(null),
      ])
      if (current !== generation.current) return
      setManager(options.can_manage_periods === true)
      setRecords(services.records || [])
      const row = check?.period || { revision: 0 }
      setPeriod(row)
      setUnknown(check?.unchecked_repeats || 0)
      periodForm.resetFields()
      periodForm.setFieldsValue({
        ...row,
        released_orders: row.released_orders == null ? null : Number(row.released_orders),
        turnover: row.turnover == null ? null : Number(row.turnover),
        remake_denominator: row.remake_denominator == null ? null : Number(row.remake_denominator),
      })
      setPeriodDirty(false)
    } catch (e) {
      if (current === generation.current) setError(errorText(e))
    } finally {
      if (current === generation.current) setBusy(false)
    }
  }, [month, periodForm])

  useEffect(() => {
    if (open) load()
    return () => {
      generation.current++
    }
  }, [open, load])

  const discard = (action: () => void) => {
    if (!periodDirty) {
      action()
      return
    }
    Modal.confirm({
      title: 'Оставить изменения месяца без сохранения?',
      okText: 'Не сохранять',
      cancelText: 'Вернуться',
      onOk: action,
    })
  }
  const editService = (record: any = null) => {
    if (periodDirty) {
      message.warning('Сначала сохраните проверку месяца или отмените её изменения')
      return
    }
    setEditorError(null)
    serviceForm.resetFields()
    requestId.current = uuid()
    if (record)
      serviceForm.setFieldsValue({
        ...record,
        registered_on: dayjs(record.registered_on),
        payments: (record.payments || []).map(payment => ({
          ...payment,
          amount: Number(payment.amount),
          paid_on: dayjs(payment.paid_on),
        })),
      })
    else
      serviceForm.setFieldsValue({
        registered_on: month.isSame(dayjs(), 'month') ? dayjs() : month.endOf('month'),
        kind: 'repair',
        payments: [],
      })
    setEditor(record || null)
  }
  const saveService = async () => {
    if (editorBusy) return
    let values
    try {
      values = await serviceForm.validateFields()
    } catch {
      return
    }
    setEditorBusy(true)
    setEditorError(null)
    try {
      await fetchAPI(
        editor ? `/api/claims-accounting/services/${editor.id}` : '/api/claims-accounting/services',
        {
          method: editor ? 'PUT' : 'POST',
          body: JSON.stringify({
            ...values,
            registered_on: values.registered_on.format('YYYY-MM-DD'),
            payments: (values.payments || []).map(payment => ({
              ...payment,
              paid_on: payment.paid_on.format('YYYY-MM-DD'),
            })),
            revision: editor?.revision || 0,
            request_id: requestId.current,
          }),
        },
      )
      setEditor(undefined)
      message.success('Сервисное обращение сохранено')
      await load()
    } catch (e) {
      setEditorError(errorText(e))
    } finally {
      setEditorBusy(false)
    }
  }
  const savePeriod = async () => {
    if (saving) return
    let values
    try {
      values = await periodForm.validateFields()
    } catch {
      return
    }
    setSaving(true)
    setError(null)
    try {
      const result = await fetchAPI(`/api/claims-accounting/period/${month.format('YYYY-MM')}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...values,
          released_orders: values.released_orders ?? null,
          turnover: values.turnover ?? null,
          remake_denominator: values.remake_denominator ?? null,
          revision: period.revision,
          services_complete: !!values.services_complete,
          repeats_complete: !!values.repeats_complete,
          remakes_complete: !!values.remakes_complete,
          visits_complete: !!values.visits_complete,
        }),
      })
      setPeriod(result.period)
      setPeriodDirty(false)
      message.success('Проверка месяца сохранена')
    } catch (e) {
      setError(errorText(e))
    } finally {
      setSaving(false)
    }
  }
  const removeService = async (row: any) => {
    if (periodDirty) {
      message.warning('Сначала сохраните проверку месяца или отмените её изменения')
      return
    }
    try {
      await fetchAPI(`/api/claims-accounting/services/${row.id}`, {
        method: 'DELETE',
        body: JSON.stringify({ revision: row.revision }),
      })
      await load()
    } catch (e) {
      message.error(errorText(e))
    }
  }
  return (
    <>
      <Modal
        title="Учёт показателей рекламаций"
        open={open}
        onCancel={() => discard(onClose)}
        width={1060}
        footer={null}
        maskClosable={false}
        destroyOnHidden
        closable={!saving && !editorBusy}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <DatePicker
            picker="month"
            value={month}
            allowClear={false}
            format="MMMM YYYY"
            disabled={saving || busy}
            disabledDate={date => date.isAfter(dayjs(), 'month')}
            onChange={value => value && discard(() => setMonth(value.startOf('month')))}
          />
          {error && (
            <Alert
              type="error"
              message={error}
              action={
                <Button onClick={load} disabled={saving}>
                  Обновить
                </Button>
              }
            />
          )}
          <Spin spinning={busy}>
            <Tabs
              items={[
                {
                  key: 'services',
                  label: 'Сервисные обращения',
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                      <Typography.Text type="secondary">
                        Здесь учитывается платный сервис. Гарантийные работы по рекламациям в этот
                        список не входят. Список включает обращения и оплаты выбранного месяца;
                        суммы показаны только за этот месяц.
                      </Typography.Text>
                      <Button icon={<PlusOutlined />} onClick={() => editService()}>
                        Добавить обращение
                      </Button>
                      <Table
                        size="small"
                        rowKey="id"
                        dataSource={records}
                        scroll={{ x: 760 }}
                        pagination={{ pageSize: 10 }}
                        columns={[
                          {
                            title: 'Дата',
                            dataIndex: 'registered_on',
                            render: value => dayjs(value).format('DD.MM.YYYY'),
                            width: 110,
                          },
                          {
                            title: 'Обращение',
                            dataIndex: 'title',
                            render: (value, row) => (
                              <>
                                <Button
                                  type="link"
                                  style={{ padding: 0 }}
                                  onClick={() => editService(row)}
                                >
                                  {value}
                                </Button>
                                <br />
                                <Typography.Text type="secondary">
                                  {kinds.find(k => k.value === row.kind)?.label}
                                  {row.invoice_number ? ` · Счёт ${row.invoice_number}` : ''}
                                </Typography.Text>
                              </>
                            ),
                          },
                          {
                            title: 'Получено за месяц, ₽',
                            key: 'paid_amount',
                            align: 'right',
                            render: (_, row) =>
                              row.payments?.length
                                ? money(
                                    row.payments
                                      .filter(p => dayjs(p.paid_on).isSame(month, 'month'))
                                      .reduce((sum, p) => sum + Number(p.amount), 0),
                                  )
                                : '—',
                            width: 130,
                          },
                          {
                            title: 'Даты оплат',
                            key: 'paid_on',
                            render: (_, row) =>
                              (row.payments || [])
                                .filter(p => dayjs(p.paid_on).isSame(month, 'month'))
                                .map(p => dayjs(p.paid_on).format('DD.MM.YYYY'))
                                .join(', ') || '—',
                            width: 120,
                          },
                          {
                            title: '',
                            key: 'actions',
                            width: 90,
                            render: (_, row) => (
                              <Popconfirm
                                title="Убрать обращение из учёта?"
                                onConfirm={() => removeService(row)}
                                okText="Убрать"
                                cancelText="Отмена"
                              >
                                <Button type="text" danger>
                                  Убрать
                                </Button>
                              </Popconfirm>
                            ),
                          },
                        ]}
                      />
                    </Space>
                  ),
                },
                ...(manager
                  ? [
                      {
                        key: 'period',
                        label: 'Проверка месяца',
                        children: (
                          <Form
                            form={periodForm}
                            layout="vertical"
                            onValuesChange={() => setPeriodDirty(true)}
                          >
                            <Alert
                              type="info"
                              showIcon
                              message="Заполняйте только подтверждённые данные"
                              description="Пустое поле означает, что источник ещё не сверили. Ноль — проверенное нулевое значение. Для месячных процентов используется полный календарный месяц."
                              style={{ marginBottom: 20 }}
                            />
                            <Row gutter={20}>
                              <Col xs={24} md={8}>
                                <Form.Item
                                  name="released_orders"
                                  label="Фактический выпуск заказов, шт."
                                >
                                  <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={8}>
                                <Form.Item name="turnover" label="Оборот: изделия + доставка, ₽">
                                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={8}>
                                <Form.Item
                                  name="remake_denominator"
                                  label="Знаменатель доли переделок"
                                >
                                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                                </Form.Item>
                              </Col>
                            </Row>
                            <Form.Item
                              name="source_note"
                              label="Источник данных и основание проверки"
                              rules={[
                                {
                                  required: true,
                                  whitespace: true,
                                  message: 'Укажите отчёт, файл или другое основание проверки',
                                },
                              ]}
                            >
                              <Input.TextArea
                                rows={2}
                                maxLength={2000}
                                placeholder="Название отчёта, период и что было сверено. Для доли переделок — что используется в знаменателе."
                              />
                            </Form.Item>
                            <Form.Item name="remakes_complete" valuePropName="checked">
                              <Checkbox>
                                Количество изделий с переделкой /30 сверено с отчётом за месяц
                              </Checkbox>
                            </Form.Item>
                            <Form.Item name="services_complete" valuePropName="checked">
                              <Checkbox>Все сервисные обращения и оплаты за месяц внесены</Checkbox>
                            </Form.Item>
                            <Form.Item name="repeats_complete" valuePropName="checked">
                              <Checkbox disabled={unknown > 0}>
                                Повторные переделки за месяц проверены
                                {unknown > 0 ? ` — осталось проверить ${unknown}` : ''}
                              </Checkbox>
                            </Form.Item>
                            <Form.Item name="visits_complete" valuePropName="checked">
                              <Checkbox>
                                Все фактические выезды за месяц внесены, включая работы без
                                рекламаций
                              </Checkbox>
                            </Form.Item>
                            <Typography.Paragraph type="secondary">
                              Изменение исходных записей снимает соответствующую отметку проверки.
                              История сохранений остаётся в ERP.
                            </Typography.Paragraph>
                            <Button
                              type="primary"
                              loading={saving}
                              disabled={busy || !periodDirty}
                              onClick={savePeriod}
                            >
                              Сохранить проверку месяца
                            </Button>
                          </Form>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </Spin>
        </Space>
      </Modal>
      <Drawer
        title={editor ? 'Изменить сервисное обращение' : 'Новое сервисное обращение'}
        open={editor !== undefined}
        onClose={() => !editorBusy && setEditor(undefined)}
        width={540}
        destroyOnHidden
        maskClosable={false}
        footer={
          <Space>
            <Button onClick={() => setEditor(undefined)} disabled={editorBusy}>
              Отмена
            </Button>
            <Button type="primary" loading={editorBusy} onClick={saveService}>
              Сохранить
            </Button>
          </Space>
        }
      >
        {editorError && <Alert type="error" message={editorError} style={{ marginBottom: 16 }} />}
        <Form form={serviceForm} layout="vertical">
          <Form.Item name="registered_on" label="Дата обращения" rules={[{ required: true }]}>
            <DatePicker
              style={{ width: '100%' }}
              format="DD.MM.YYYY"
              disabledDate={date => date.isAfter(dayjs(), 'day')}
            />
          </Form.Item>
          <Form.Item name="title" label="Описание" rules={[{ required: true, whitespace: true }]}>
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>
          <Form.Item name="kind" label="Вид работы" rules={[{ required: true }]}>
            <Select options={kinds} />
          </Form.Item>
          <Form.Item name="invoice_number" label="Номер счёта WD">
            <Input maxLength={255} />
          </Form.Item>
          <Typography.Paragraph>Полученные оплаты</Typography.Paragraph>
          <Form.List name="payments">
            {(fields, { add, remove }) => (
              <>
                {fields.map(field => (
                  <Row gutter={12} key={field.key} align="middle">
                    <Form.Item name={[field.name, 'id']} hidden>
                      <Input />
                    </Form.Item>
                    <Col span={10}>
                      <Form.Item
                        name={[field.name, 'amount']}
                        label="Сумма, ₽"
                        rules={[{ required: true }]}
                      >
                        <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={11}>
                      <Form.Item
                        name={[field.name, 'paid_on']}
                        label="Дата оплаты"
                        rules={[{ required: true }]}
                      >
                        <DatePicker
                          format="DD.MM.YYYY"
                          disabledDate={date => date.isAfter(dayjs(), 'day')}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Button
                        type="text"
                        aria-label="Убрать оплату"
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    </Col>
                  </Row>
                ))}
                <Button onClick={() => add()} icon={<PlusOutlined />} style={{ marginBottom: 16 }}>
                  Добавить оплату
                </Button>
              </>
            )}
          </Form.List>
          <Typography.Paragraph type="secondary">
            Можно внести несколько частичных оплат в разные даты. Если оплата ещё не подтверждена,
            не добавляйте её.
          </Typography.Paragraph>
          <Form.Item name="comment" label="Комментарий">
            <Input.TextArea rows={3} maxLength={2000} />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  )
}
